"use client";

import { RiMusic2Line, RiRainbowLine, RiTicketLine } from "@remixicon/react";
import { useQueryState } from "nuqs";
import { startTransition, useMemo, useState } from "react";
import { ArtistContentSkeleton } from "@/components/features/artist/content/artist-content-skeleton";
import { ArtistErrorBoundaryWrapper } from "@/components/features/artist/error-boundary";
import {
	ArtistEmptyState,
	ArtistSearchEmptyState,
} from "@/components/features/artist/states";
import { ArtistFestivalsView } from "@/components/features/artist/views/artist-festivals-view";
import { ArtistSetsView } from "@/components/features/artist/views/artist-sets-view";
import { ArtistShowsView } from "@/components/features/artist/views/artist-shows-view";
import { GroupContentTabs } from "@/components/features/group/tabs/group-content-tabs";
import { ProfileYearSelector } from "@/components/features/profile/tabs/profile-year-selector";
import { EnhancedSearchInput } from "@/components/shared/filters/search/enhanced-search-input";
import {
	type SortOption,
	UniversalSortDropdown,
} from "@/components/shared/filters/search/universal-sort-dropdown";
import { useSearchInput } from "@/hooks/shared/use-search-input";
import { trpc } from "@/lib/trpc/react";
import {
	artistContentTypeParser,
	profileYearParser,
} from "@/lib/utils/url-parsers";

interface GroupContentProps {
	groupSlug: string;
	groupName: string;
}

export function GroupContent({ groupSlug, groupName }: GroupContentProps) {
	// URL state for content type and year management using nuqs - now supports all 3 types
	const [activeContentType, setActiveContentType] = useQueryState(
		"type",
		artistContentTypeParser,
	);
	const [selectedYear, setSelectedYear] = useQueryState(
		"year",
		profileYearParser,
	);

	// Search states for each tab using useSearchInput hook
	const festivalsSearch = useSearchInput({ debounceMs: 300 });
	const setsSearch = useSearchInput({ debounceMs: 300 });
	const showsSearch = useSearchInput({ debounceMs: 300 });

	// Sort state
	const [festivalsSort, setFestivalsSort] = useState("date-desc");
	const [setsSort, setSetsSort] = useState("date-desc");
	const [showsSort, setShowsSort] = useState("date-desc");

	// Sort options (all tabs share same options)
	const dateSortOptions: SortOption[] = [
		{ value: "date-desc", label: "Upcoming First" },
		{ value: "date-asc", label: "Oldest First" },
	];

	// Parse sort values
	const parseSortValue = (sortValue: string) => {
		const parts = sortValue.split("-");
		const direction = parts[parts.length - 1];
		return {
			sortBy: "date",
			sortAscending: direction === "asc",
		};
	};

	const festivalsSortParsed = parseSortValue(festivalsSort);
	const setsSortParsed = parseSortValue(setsSort);
	const showsSortParsed = parseSortValue(showsSort);

	// Get actual counts from server - always fetch this
	const { data: countsData } = trpc.group.getCounts.useQuery(
		{
			slug: groupSlug,
		},
		{
			staleTime: 1 * 60 * 1000,
		},
	);

	// Get available years for filtering - always fetch this
	const { data: availableYearsData } = trpc.group.getAvailableYears.useQuery(
		{
			slug: groupSlug,
		},
		{
			staleTime: 5 * 60 * 1000,
			refetchOnWindowFocus: false,
		},
	);

	// Fetch festivals where group has performed
	const festivalsEnabled = activeContentType === "festivals";
	const {
		data: festivalsData,
		isLoading: festivalsLoading,
		error: festivalsError,
		hasNextPage: festivalsHasNextPage,
		fetchNextPage: festivalsFetchNextPage,
		isFetchingNextPage: festivalsIsFetchingNextPage,
	} = trpc.group.getFestivals.useInfiniteQuery(
		{
			slug: groupSlug,
			year: selectedYear,
			search: festivalsSearch.debouncedValue,
			sortBy: "date",
			sortAscending: festivalsSortParsed.sortAscending,
			limit: 12,
		},
		{
			enabled: festivalsEnabled,
			getNextPageParam: (lastPage) => lastPage.nextCursor,
			staleTime: 2 * 60 * 1000,
			gcTime: 15 * 60 * 1000,
			refetchOnWindowFocus: false,
		},
	);

	// Fetch group sets with pagination
	const setsEnabled = activeContentType === "sets";
	const {
		data: setsData,
		isLoading: setsLoading,
		error: setsError,
		hasNextPage: setsHasNextPage,
		fetchNextPage: setsFetchNextPage,
		isFetchingNextPage: setsIsFetchingNextPage,
	} = trpc.group.getSets.useInfiniteQuery(
		{
			slug: groupSlug,
			year: selectedYear,
			search: setsSearch.debouncedValue,
			sortBy: "date",
			sortAscending: setsSortParsed.sortAscending,
			limit: 12,
		},
		{
			enabled: setsEnabled,
			getNextPageParam: (lastPage) => lastPage.nextCursor,
			staleTime: 1 * 60 * 1000,
			gcTime: 10 * 60 * 1000,
			refetchOnWindowFocus: false,
		},
	);

	// Fetch group shows with pagination
	const showsEnabled = activeContentType === "shows";
	const {
		data: showsData,
		isLoading: showsLoading,
		error: showsError,
		hasNextPage: showsHasNextPage,
		fetchNextPage: showsFetchNextPage,
		isFetchingNextPage: showsIsFetchingNextPage,
	} = trpc.group.getShows.useInfiniteQuery(
		{
			slug: groupSlug,
			year: selectedYear,
			search: showsSearch.debouncedValue,
			sortBy: "date",
			sortAscending: showsSortParsed.sortAscending,
			limit: 8,
		},
		{
			enabled: showsEnabled,
			getNextPageParam: (lastPage) => lastPage.nextCursor,
			staleTime: 3 * 60 * 1000,
			gcTime: 12 * 60 * 1000,
			refetchOnWindowFocus: false,
		},
	);

	// Transform sets to match expected ArtistSet format
	const setsArray = useMemo(() => {
		if (!setsData?.pages) return [];

		// Extract all sets from paginated data
		const allSets = setsData.pages.flatMap((page) => page.items);

		// Transform to match ArtistSet interface
		return allSets.map((set) => ({
			id: set.set_id,
			startTime: set.set_start_time,
			endTime: set.set_end_time,
			artist: {
				id: set.artist_object?.id || "",
				name: set.artist_object?.name || groupName, // Use backend group name
				slug: groupSlug,
				image_url: set.artist_object?.image_url || null,
			},
			festival: {
				id: set.festival_id || "",
				name: set.festival_name || "",
				slug: set.festival_slug || "",
				start_date: set.festival_start_date || set.set_start_time || "",
				end_date: set.festival_end_date || set.set_start_time || "",
				poster_url: set.festival_poster_url || null,
			},
			stage:
				set.stage_id && set.stage_name
					? {
							id: set.stage_id,
							name: set.stage_name,
						}
					: null,
			// Use actual backend data instead of hardcoded values
			friends_set: set.friends_set || false,
			special_guests: set.special_guests || null,
			is_collaborative: set.is_collaborative || false,
			display_format: (set.display_format as "B2B" | "x" | "+" | "F2F" | "&" | "vs" | "B3B" | "with" | null) || null,
			alias_name: set.set_alias_name,
			is_group_alias: set.is_group_alias || false,
			presents_name: set.presents_name || null,
			// Group alias data
			group_alias_name: set.group_alias_name || null,
			member_artists: set.member_artists || [],
			// Transform set_artists to match expected format
			set_artists: (set.set_artists || []).map((sa) => ({
				artist_id: sa.artist_id,
				position: sa.position,
				artist_name: sa.artist.name,
			})),
		}));
	}, [setsData, groupName, groupSlug]);

	// Extract facepile data for sets
	const setsFacepileData = useMemo(() => {
		if (!setsData?.pages) return {};

		// Merge facepile data from all pages
		const mergedData: Record<
			string,
			{
				id: string;
				username: string | null;
				display_name: string;
				google_avatar_url?: string | null;
				manual_avatar_url?: string | null;
			}[]
		> = {};
		setsData.pages.forEach((page) => {
			if (page.facepileData) {
				Object.assign(mergedData, page.facepileData);
			}
		});

		return mergedData;
	}, [setsData]);

	// Extract festivals from paginated data
	const festivalsArray = useMemo(() => {
		// Extract items from all pages and add required fields
		return (
			festivalsData?.pages?.flatMap((page) =>
				page.items.map((item) => ({
					...item,
					description: null, // Groups don't have festival descriptions
					location: item.location || "", // Handle null location
				})),
			) || []
		);
	}, [festivalsData]);

	// Extract facepile data for festivals
	const festivalsFacepileData = useMemo(() => {
		if (!festivalsData?.pages) return {};

		// Merge facepile data from all pages
		const mergedInteractions: Record<
			string,
			{
				id: string;
				username: string | null;
				display_name: string;
				google_avatar_url?: string | null;
				manual_avatar_url?: string | null;
			}[]
		> = {};
		const mergedSets: Record<
			string,
			{
				id: string;
				start_time: string | null;
				end_time: string | null;
				alias_name: string | null;
				artist: {
					id: string;
					name: string;
					image_url: string | null;
				};
			}
		> = {};

		festivalsData.pages.forEach((page) => {
			if (page.facepileData) {
				// Handle new data structure
				if (
					"interactions" in page.facepileData &&
					"sets" in page.facepileData
				) {
					Object.assign(mergedInteractions, page.facepileData.interactions);
					Object.assign(mergedSets, page.facepileData.sets);
				} else {
					// Fallback for old data structure
					Object.assign(
						mergedInteractions,
						page.facepileData as Record<
							string,
							{
								id: string;
								username: string | null;
								display_name: string;
								google_avatar_url?: string | null;
								manual_avatar_url?: string | null;
							}[]
						>,
					);
				}
			}
		});

		return {
			interactions: mergedInteractions,
			sets: mergedSets,
		};
	}, [festivalsData]);

	// Extract shows from paginated data
	const showsArray = useMemo(() => {
		return (
			showsData?.pages?.flatMap((page) =>
				page.items.map((item) => ({
					...item,
					name: item.title, // Map title to name
					venue: {
						id: item.venue_id || "unknown",
						name: item.venue_name || "Unknown Venue",
						city: item.venue_city || undefined,
						state: item.venue_state || undefined,
						country: item.venue_country || undefined,
					},
					artists: [
						{
							id: "group",
							name: groupName,
							slug: groupSlug,
						},
					], // Add artists array for group
				})),
			) || []
		);
	}, [showsData, groupName, groupSlug]);

	// Extract facepile data for shows
	const showsFacepileData = useMemo(() => {
		if (!showsData?.pages) return {};

		// Merge facepile data from all pages
		const mergedData: Record<
			string,
			{
				id: string;
				username: string | null;
				display_name: string;
				google_avatar_url?: string | null;
				manual_avatar_url?: string | null;
			}[]
		> = {};
		showsData.pages.forEach((page) => {
			if (page.facepileData) {
				Object.assign(mergedData, page.facepileData);
			}
		});

		return mergedData;
	}, [showsData]);

	// Get available years from server data
	const availableYears =
		availableYearsData?.years.map((year) => parseInt(year, 10)) || [];

	// Calculate counts - use server data if available, otherwise fallback
	const counts = useMemo(() => {
		if (countsData) {
			return countsData;
		}
		// Fallback to loaded data
		return {
			festivals: festivalsArray.length,
			sets: setsArray.length,
			shows: showsArray.length,
		};
	}, [countsData, festivalsArray.length, setsArray.length, showsArray.length]);

	// Event handlers
	const handleContentTypeChange = (value: string) => {
		startTransition(() => {
			setActiveContentType(value as "festivals" | "sets" | "shows");
			setSelectedYear("all");
		});
	};

	const handleYearChange = (value: string) => {
		startTransition(() => {
			setSelectedYear(value);
		});
	};

	// Default to festivals if not set
	const displayContentType = activeContentType || "festivals";

	return (
		<div className="flex flex-1 flex-col">
			{/* Tab Navigation */}
			<GroupContentTabs
				activeContentType={displayContentType as "festivals" | "sets" | "shows"}
				onContentTypeChange={handleContentTypeChange}
				counts={counts}
			/>

			<div>
				{displayContentType === "festivals" && (
					<StickyFilterRow
						selectedYear={selectedYear}
						onYearChange={handleYearChange}
						availableYears={availableYears.length > 0 ? availableYears : undefined}
						sortValue={festivalsSort}
						onSortChange={setFestivalsSort}
						sortOptions={dateSortOptions}
						searchPlaceholder="Search festivals..."
						search={festivalsSearch}
					/>
				)}
				{displayContentType === "sets" && (
					<StickyFilterRow
						selectedYear={selectedYear}
						onYearChange={handleYearChange}
						availableYears={availableYears.length > 0 ? availableYears : undefined}
						sortValue={setsSort}
						onSortChange={setSetsSort}
						sortOptions={dateSortOptions}
						searchPlaceholder="Search by festival, stage, or artist..."
						search={setsSearch}
					/>
				)}
				{displayContentType === "shows" && (
					<StickyFilterRow
						selectedYear={selectedYear}
						onYearChange={handleYearChange}
						availableYears={availableYears.length > 0 ? availableYears : undefined}
						sortValue={showsSort}
						onSortChange={setShowsSort}
						sortOptions={dateSortOptions}
						searchPlaceholder="Search shows or venues..."
						search={showsSearch}
					/>
				)}

				{/* Content based on active tab */}
				{displayContentType === "festivals" && (
					<ArtistErrorBoundaryWrapper>
						{festivalsLoading ? (
							<ArtistContentSkeleton type="festivals" />
						) : festivalsError ? (
							<div className="flex items-center justify-center py-8">
								<div className="text-sm text-red-500">
									Error loading festivals: {festivalsError.message}
								</div>
							</div>
						) : festivalsArray.length === 0 ? (
							festivalsSearch.debouncedValue || selectedYear !== "all" ? (
								<ArtistSearchEmptyState
									searchTerm={festivalsSearch.debouncedValue}
									selectedYear={selectedYear || "all"}
									contentType="festivals"
									artistName={groupName}
									onClearSearch={() => festivalsSearch.setValue("")}
									onClearYear={() => setSelectedYear("all")}
								/>
							) : (
								<ArtistEmptyState
									icon={RiRainbowLine}
									title={`${groupName} hasn't performed at any festivals yet`}
									description="When they perform at festivals, they'll show up here"
								/>
							)
						) : (
							<ArtistFestivalsView
								festivals={festivalsArray}
								artistId=""
								artistName={groupName}
								fetchNextPage={festivalsFetchNextPage}
								hasNextPage={festivalsHasNextPage}
								isFetchingNextPage={festivalsIsFetchingNextPage}
								selectedYear={selectedYear}
								searchTerm={festivalsSearch.debouncedValue}
								sortBy={festivalsSort}
								onClearSearch={() => festivalsSearch.setValue("")}
								onClearYear={() => setSelectedYear("all")}
								isActive={displayContentType === "festivals"}
								facepileData={festivalsFacepileData}
							/>
						)}
					</ArtistErrorBoundaryWrapper>
				)}

				{/* Sets View */}
				{displayContentType === "sets" && (
					<ArtistErrorBoundaryWrapper>
						{setsLoading ? (
							<ArtistContentSkeleton type="sets" />
						) : setsError ? (
							<div className="flex items-center justify-center py-8">
								<div className="text-sm text-red-500">
									Error loading sets: {setsError.message}
								</div>
							</div>
						) : setsArray.length === 0 ? (
							setsSearch.debouncedValue || selectedYear !== "all" ? (
								<ArtistSearchEmptyState
									searchTerm={setsSearch.debouncedValue}
									selectedYear={selectedYear || "all"}
									contentType="sets"
									artistName={groupName}
									onClearSearch={() => setsSearch.setValue("")}
									onClearYear={() => setSelectedYear("all")}
								/>
							) : (
								<ArtistEmptyState
									icon={RiMusic2Line}
									title={`${groupName} hasn't performed any sets yet`}
									description="When they perform sets at festivals, they'll show up here"
								/>
							)
						) : (
							<ArtistSetsView
								sets={setsArray}
								artistName={groupName}
								fetchNextPage={setsFetchNextPage}
								hasNextPage={setsHasNextPage}
								isFetchingNextPage={setsIsFetchingNextPage}
								selectedYear={selectedYear}
								searchTerm={setsSearch.debouncedValue}
								sortBy={setsSort}
								onClearSearch={() => setsSearch.setValue("")}
								onClearYear={() => setSelectedYear("all")}
								isActive={displayContentType === "sets"}
								facepileData={setsFacepileData}
							/>
						)}
					</ArtistErrorBoundaryWrapper>
				)}

				{/* Shows View */}
				{displayContentType === "shows" && (
					<ArtistErrorBoundaryWrapper>
						{showsLoading ? (
							<ArtistContentSkeleton type="shows" />
						) : showsError ? (
							<div className="flex items-center justify-center py-8">
								<div className="text-sm text-red-500">
									Error loading shows: {showsError.message}
								</div>
							</div>
						) : showsArray.length === 0 ? (
							showsSearch.debouncedValue || selectedYear !== "all" ? (
								<ArtistSearchEmptyState
									searchTerm={showsSearch.debouncedValue}
									selectedYear={selectedYear || "all"}
									contentType="shows"
									artistName={groupName}
									onClearSearch={() => showsSearch.setValue("")}
									onClearYear={() => setSelectedYear("all")}
								/>
							) : (
								<ArtistEmptyState
									icon={RiTicketLine}
									title={`${groupName} hasn't performed any shows yet`}
									description="When they perform individual shows, they'll show up here"
								/>
							)
						) : (
							<ArtistShowsView
								shows={showsArray}
								artistId=""
								artistName={groupName}
								fetchNextPage={showsFetchNextPage}
								hasNextPage={showsHasNextPage}
								isFetchingNextPage={showsIsFetchingNextPage}
								selectedYear={selectedYear}
								searchTerm={showsSearch.debouncedValue}
								sortBy={showsSort}
								onClearSearch={() => showsSearch.setValue("")}
								onClearYear={() => setSelectedYear("all")}
								isActive={displayContentType === "shows"}
								facepileData={showsFacepileData}
							/>
						)}
					</ArtistErrorBoundaryWrapper>
				)}
			</div>
		</div>
	);
}

function StickyFilterRow({
	selectedYear,
	onYearChange,
	availableYears,
	sortValue,
	onSortChange,
	sortOptions,
	searchPlaceholder,
	search,
}: {
	selectedYear: string | null;
	onYearChange: (value: string) => void;
	availableYears?: number[];
	sortValue: string;
	onSortChange: (value: string) => void;
	sortOptions: SortOption[];
	searchPlaceholder: string;
	search: ReturnType<typeof useSearchInput>;
}) {
	return (
		<div className="sticky top-[3.5rem] z-30 bg-background py-4 md:top-[calc(3.5rem+env(safe-area-inset-top))]">
			<div className="space-y-3">
				<div className="flex items-center gap-3">
					<ProfileYearSelector
						selectedYear={selectedYear ?? "all"}
						onYearChange={onYearChange}
						availableYears={availableYears}
						className="min-w-0 flex-1"
					/>
					<UniversalSortDropdown
						value={sortValue}
						onValueChange={onSortChange}
						options={sortOptions}
						className="min-w-0 flex-1"
					/>
				</div>
				<EnhancedSearchInput
					placeholder={searchPlaceholder}
					className="flex-1"
					value={search.value}
					onValueChange={(value) => search.setValue(value)}
					showLoadingIndicator={true}
					isLoading={search.isSearching}
				/>
			</div>
		</div>
	);
}
